<template>
  <div class="pdl-file-upload">
    <div :class="{'border border-red': hasError}" :style="`--aspect-ratio:calc(${aspectRatio})`" :qaid="qaid">
      <div v-if="displayOnly">
        <div :style="`--aspect-ratio:calc(${aspectRatio})`">
          <span class="file-uploads">
            <img v-if="isImage" :src="presetImageFileUrl" alt="" @error="reloadImage" @load="imageLoaded" />

            <pdl-icon v-else name="insert_drive_file" size="48" />
          </span>
        </div>

        <div>
          <pdl-button
            text
            icon-only
            icon="cancel"
            class="pdl-file-upload__remove-file-button"
            aria-label="Clear file"
            @click="clearFile"
          />
        </div>
      </div>

      <div v-else-if="cloudinaryCloudName" class="file-uploads cursor-pointer" @click="uploadToCloudinary()">
        <pdl-loading :is-loading="loading">
          <template v-if="!fileUploaded">
            <pdl-icon name="note_add" />
            <div v-html="clickToUploadMsg"></div>
          </template>
          <template v-else>
            <img :src="file" alt="" />
          </template>
        </pdl-loading>
      </div>

      <vue-upload-component
        v-else
        ref="upload"
        v-model="files"
        :name="inputId"
        :input-id="inputId"
        :headers="headers"
        :accept="accept"
        :extensions="extensions"
        :post-action="isImage ? null : postActionUrl"
        @input-file="inputFile"
      >
        <pdl-loading :is-loading="loading">
          <template v-if="!fileUploaded">
            <pdl-icon name="note_add" />

            <div v-html="clickToUploadMsg"></div>
          </template>

          <template v-else>
            <template v-if="isImage">
              <img :src="file" alt="" />
            </template>

            <span v-else>
              <pdl-icon name="insert_drive_file" size="48" />
            </span>
          </template>
        </pdl-loading>
      </vue-upload-component>

      <div v-if="fileUploaded && !loading">
        <pdl-button
          :id="buttonId"
          text
          icon-only
          icon="cancel"
          class="pdl-file-upload__remove-file-button"
          aria-label="Clear file"
          @click="clearFile"
        />
      </div>
    </div>

    <div class="mt-25">
      <div v-if="computedFileName && isFilenameVisible" class="text-sm text-gray-60 js-computed-file-name">
        {{ computedFileName }}
      </div>

      <!-- @slot Slot for custom help message. -->

      <slot name="help">
        <div v-if="help" class="text-sm text-gray-60">{{ help }}</div>
      </slot>
    </div>

    <div v-if="uploadButton" class="buttons">
      <pdl-button
        v-if="!$refs.upload || !$refs.upload.active"
        secondary
        small
        icon="cloud_upload"
        qaid="start-upload"
        @click.prevent="activateUpload"
      >
        <span>{{ uploadButtonText }}</span>
      </pdl-button>
    </div>
  </div>
</template>

<script>
// https://lian-yue.github.io/vue-upload-component/
import VueUploadComponent from 'vue-upload-component';
import {PdlLoading} from '@pedal/pdl-loading';
import {PdlButton} from '@pedal/pdl-button';
import {PdlIcon} from '@pedal/pdl-icon';
const imageExtensions = 'gif,jpg,jpeg,png';
const imageAcceptHeaders = 'image/png, image/gif, image/jpeg';

export default {
  name: 'PdlFileUpload',
  components: {VueUploadComponent, PdlLoading, PdlButton, PdlIcon},
  props: {
    /**
     * Help text message.
     */
    help: {
      type: String,
      default: 'Image file format only',
    },
    /**
     * Call to action message for file upload container.
     */
    clickToUploadMsg: {
      type: String,
      default: 'Select file',
    },
    /**
     * Wrong file type message (via Toast).
     */
    wrongFileTypeMsg: {
      type: String,
      default: 'Wrong file format selected',
    },
    /**
     * File exceeds maximum file size message (via Toast).
     */
    fileTooLargeMsg: {
      type: String,
      default: 'File is too large',
    },
    /**
     * File is ready to upload message (via Toast).
     */
    readyToUploadMsg: {
      type: String,
      default: 'Ready to upload',
    },
    /**
     * Maximum file size allowed for upload (in bytes).
     */
    fileSize: {
      type: Number,
      // In Bytes. This is defaulted to 5000001 Bytes or 5MB
      default: 5000001,
    },
    /**
     * Aspect ratio of file upload container.
     */
    aspectRatio: {
      type: String,
      default: '1',
    },
    /**
     * ID of "Clear file" button.
     */
    buttonId: {
      type: String,
      default: undefined,
    },
    /**
     * ID of file upload input.
     */
    inputId: {
      type: String,
      default: undefined,
    },
    /**
     * Url for POST action if upload is not an image.
     */
    postAction: {
      type: String,
      default: undefined,
    },
    /**
     * Used to create url for POST action if postAction prop is not used and upload is not an image.
     */
    csrfToken: {
      type: String,
      default: undefined,
    },
    /**
     * Whether to display the upload button.
     */
    uploadButton: {
      type: Boolean,
      default: true,
    },
    /**
     * Sets text for upload button.
     */
    uploadButtonText: {
      type: String,
      default: 'Upload',
    },
    /**
     * A `qaid` is used as a unique string, serving as a hook for automated tests.
     */
    qaid: {
      type: String,
      default: 'file-upload',
    },
    /**
     * Sets file name if file is uploaded.
     */
    inFileName: {
      type: String,
      default: undefined,
    },
    /**
     * Sets image if file is uploaded.
     */
    inPresetImageFileUrl: {
      type: String,
      default: undefined,
    },
    /**
     * Adds red border to upload container when there is an error.
     */
    hasError: {
      type: Boolean,
      default: false,
    },
    /**
     * File extensions allowed to be uploaded.
     */
    extensions: {
      type: [String, Array],
      default: imageExtensions,
    },
    /**
     * Form accept attributes, MIME type.
     */
    accept: {
      type: String,
      default: imageAcceptHeaders,
    },
    /**
     * Allow for file upload to happen on form submission.
     */
    uploadOnInput: {
      type: Boolean,
      default: false,
    },
    /**
     * Designate cloud name for Cloudinary uploads.
     */
    cloudinaryCloudName: {
      type: String,
      default: '',
    },
    /**
     * Assign Cloudinary upload preset.
     */
    cloudinaryUploadPreset: {
      type: String,
      default: '',
    },
    /**
     * Generate Cloudinary signature, needed for signed uploads.
     */
    cloudinaryGenerateSignature: {
      type: Function,
      default: () => null,
    },
    /**
     * Assign Cloudinary API key, needed for signed uploads.
     */
    cloudinaryApiKey: {
      type: String,
      default: '',
    },
    /**
     * Assign aspect ratio for image cropping.
     */
    cloudinaryCroppingAspectRatio: {
      type: String,
      default: '',
    },
    /**
     * Designate which sources can be used to upload images within widget.
     */
    cloudinaryImageSource: {
      type: Array,
      default: () => ['local'],
    },
    /**
     * To show filename under the component.
     */
    isFilenameVisible: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to show toast on successfull upload.
     */
    showUploadToast: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      file: {},
      files: [],
      fileName: null,
      loading: false,
      headers: {},
      presetImageFileUrl: this.inPresetImageFileUrl,
      imageReloadInterval: null,
      widget: null,
    };
  },

  computed: {
    fileUploaded() {
      return !this.isEmpty(this.files);
    },

    computedFileName() {
      let filename = this.inFileName || this.fileName;
      if (!filename && this.presetImageFileUrl) {
        filename = this.getFilenameFromUrl(this.presetImageFileUrl);
      }

      return filename;
    },

    computedFileExtension() {
      return this.computedFileName ? this.computedFileName.split('.').pop() : null;
    },

    isImage() {
      return imageExtensions.includes(this.computedFileExtension);
    },

    postActionUrl() {
      if (!this.postAction) {
        return `${window.location.pathname}?CSRFToken=${this.csrfToken}`;
      } else {
        return this.postAction;
      }
    },

    displayOnly() {
      return this.presetImageFileUrl && this.presetImageFileUrl.length > 0;
    },

    calculatedAspectRatio() {
      return this.cloudinaryCroppingAspectRatio
        ? this.cloudinaryCroppingAspectRatio.split('/')[0] / this.cloudinaryCroppingAspectRatio.split('/')[1]
        : undefined;
    },
  },

  watch: {
    inPresetImageFileUrl() {
      this.presetImageFileUrl = this.inPresetImageFileUrl;
    },
  },

  created() {
    if (!this.cloudinaryCloudName) return;
    this.appendCloudinaryScripts();
  },
  beforeUnmount() {
    if (!this.widget) return;
    this.widget.destroy();
  },

  methods: {
    reloadImage(event) {
      if (this.imageReloadInterval == null) {
        const errEvent = event;
        let repetition = 0;
        this.imageReloadInterval = setInterval(() => {
          errEvent.target.src = this.inPresetImageFileUrl;
          repetition++;
          if (repetition > 4) {
            clearInterval(this.imageReloadInterval);
          }
        }, 4000);
      }
    },
    imageLoaded() {
      if (this.imageReloadInterval) {
        clearInterval(this.imageReloadInterval);
      }
    },
    getFilenameFromUrl(url) {
      if (!url) {
        return '';
      }

      const matches = url
        .split('?')[0]
        .toString()
        .match(/\/([^/]+)$/);
      return matches && matches.length > 1 ? matches[1] : '';
    },

    showToast(type, message, options = {}) {
      this.$notify({
        type: type,
        message: message,
        ...options,
      });
    },

    activateUpload() {
      this.$refs.upload.active = true;
      /**
       * Emitted when the upload button is clicked.
       */
      this.$emit('activate-upload');
    },

    handleFileUpload(file) {
      setTimeout(() => {
        const reader = new FileReader();

        reader.readAsDataURL(file.file);

        reader.addEventListener('load', () => {
          this.file = reader.result;
          this.fileName = file.name;
          this.loading = false;

          if (this.showUploadToast) {
            this.showToast('success', `${file.name} ${this.readyToUploadMsg}`, {
              dangerouslyUseHTMLString: true,
            });
          }

          /**
           * Emitted when the file upload is complete.
           */
          this.$emit('file-uploaded', {name: file.name, encodedFile: this.file});
        });
      }, 1000);
    },

    clearFile() {
      this.presetImageFileUrl = null;
      this.files = [];
      this.file = null;
      this.fileName = null;
      /**
       * Emitted when the file is removed.
       */
      this.$emit('clear-file');
    },

    fileExtensionCheck(newFile) {
      let newFileExt = newFile.name.slice(newFile.name.lastIndexOf('.') + 1);
      return this.extensions.includes(newFileExt.toLowerCase());
    },

    inputFileValidation(newFile) {
      if (!this.fileExtensionCheck(newFile)) {
        this.clearFile();
        this.showToast('error', this.wrongFileTypeMsg);
        return false;
      }
      if (newFile.size > this.fileSize) {
        this.clearFile();
        this.showToast('error', this.fileTooLargeMsg);
        return false;
      }
      return true;
    },

    inputFile(newFile, oldFile) {
      if (newFile && !oldFile && this.inputFileValidation(newFile)) {
        this.fileName = newFile.name;
        // handle adding file
        if (this.uploadOnInput) {
          this.handleFileUpload(newFile);
        }
      } else if (newFile && oldFile && !newFile?.active && oldFile?.active) {
        //Get response data
        this.files = [];
        /**
         * Emitted with success/error details when file upload is attempted.
         */
        this.$emit('response', newFile.response.data);
        const bulkUploadFeedback = newFile.response.meta.feedback;
        const toastMessage = bulkUploadFeedback.message.basePropertyValue;

        if (bulkUploadFeedback.method === 'toast') {
          this.showToast(bulkUploadFeedback.type ?? 'info', toastMessage);
        } else if (newFile.response.data.tooManyUploads === true) {
          this.showToast('error', toastMessage);
        } else if (newFile.response.data.successCount > 0) {
          this.showToast('success', toastMessage);
        }
      }
      if (newFile && oldFile) {
        // handle updating file
      }
      if (!newFile && oldFile) {
        // handle removing the file here
      }
    },

    appendCloudinaryScripts() {
      const widgetScript = document.createElement('script');
      widgetScript.src = 'https://widget.cloudinary.com/v2.0/global/all.js';
      widgetScript.type = 'text/javascript';
      document.head.appendChild(widgetScript);
    },

    uploadToCloudinary() {
      this.widget = window.cloudinary?.createUploadWidget(
        //https://cloudinary.com/documentation/upload_widget_reference#parameters
        {
          cloudName: this.cloudinaryCloudName,
          apiKey: this.cloudinaryApiKey,
          uploadPreset: this.cloudinaryUploadPreset, //configurable preset from Cloudinary console
          showPoweredBy: false,
          theme: 'minimal',
          ...(this.cloudinaryGenerateSignature() && {uploadSignature: this.cloudinaryGenerateSignature}),
          cropping: !!this.cloudinaryCroppingAspectRatio,
          croppingAspectRatio: this.calculatedAspectRatio,
          showSkipCropButton: false,
          sources: this.cloudinaryImageSource,
          multiple: false,
          clientAllowedFormats: this.extensions,
          maxFileSize: 40000000,
          styles: {
            palette: {
              link: getComputedStyle(document.body).getPropertyValue('--gray-100'),
              action: getComputedStyle(document.body).getPropertyValue('--gray-100'),
              error: getComputedStyle(document.body).getPropertyValue('--red-100'),
            },
          },
        },
        (error, result) => {
          if (!error && result && result.event === 'success') {
            this.file = result.info.secure_url;
            this.files = result.info.secure_url;
            this.$emit('file-uploaded', result.info);
          }
        }
      );
      this.widget?.open();
    },
    isEmpty(obj) { 
      return !Object.entries(obj || {}).length && !obj?.length && !obj?.size
    },
  },
};
</script>

<style lang="scss">
@import 'FileUpload.scss';
</style>
